import { defineStore } from 'pinia'

interface Order {
  _id: string
  name: string
  phone: string
  email: string
  checkIn: Date | null
  checkOut: Date | null
  room: string
  date: Date
}

interface OrdersState {
  items: Order[]
  item: Order
  loading: boolean
  error: null | Error
}

export const useOrdersStore = defineStore('orders', {
  state: (): OrdersState => ({
    items: [],
    item: {
      _id: '',
      name: '',
      phone: '',
      email: '',
      checkIn: null,
      checkOut: null,
      room: '',
      date: new Date
    },
    loading: false,
    error: null
  }),

  actions: {
    setLoading(value: boolean) {
      this.loading = value
    },

    setError(error: Error) {
      this.error = error
    },

    async fetchOrders() {
      try {
        this.setLoading(true)
        const { data } = await useFetch<Order[]>('https://api.leonhotel.ru/api/v1/orders')
        if (data.value) {
          this.items = data.value
        }
      } catch (err: any) {
        this.setError(new Error(err.message))
      } finally {
        this.setLoading(false)
      }
    },

    async getOne(_id: string) {
      try {
        const data = await $fetch(`https://api.leonhotel.ru/api/v1/orders${_id}`)
        this.item = data
      } catch (err: any) {
        this.error = new Error(err.message)
      }
    },

    async setOne(order: OrderItem) {
      try {
        const response = await $fetch('https://api.leonhotel.ru/api/v1/orders', {
          method: 'POST',
          body: order
        })
        this.success = true
        return response.status
      } catch (err: any) {
        this.error = new Error(err.message)
      }
    },

    async submitOrder() {
      try {
        await this.setOne(this.item)
        this.setSuccess(true)
        this.resetForm()
      } catch (err: any) {
        this.error = new Error(err.message)
      }
    },

    setSuccess(value: boolean) {
      this.success = value
    },

    setRoom(value: string) {
      this.item.room = value
    },

    setName(value: string) {
      this.item.name = value
    },

    setCheckIn(value: Date | null) {
      this.item.checkIn = value
    },

    setCheckOut(value: Date | null) {
      this.item.checkOut = value
    },

    setPhone(value: string) {
      this.item.phone = value
    },

    setEmail(value: string) {
      this.item.email = value
    },

    resetForm() {
      this.item = {
        name: '',
        checkIn: null,
        checkOut: null,
        phone: '',
        email: '',
        room: ''
      }
    }
  },

  getters: {
    today: (state) => {
      let res = 0
      const ourDate = new Date()
      ourDate.setDate(ourDate.getDate() - 1)
      const yesterday = new Date(ourDate)
      
      state.items.forEach(item => {
        const date = new Date(item.date!)
        if (date > yesterday) res++
      })
      return res
    },

    thisWeek: (state) => {
      let res = 0
      const ourDate = new Date()
      ourDate.setDate(ourDate.getDate() - 7)
      const week = new Date(ourDate)
      
      state.items.forEach(item => {
        const date = new Date(item.date!)
        if (date > week) res++
      })
      return res
    },

    thisMonth: (state) => {
      let res = 0
      const ourDate = new Date()
      ourDate.setMonth(ourDate.getMonth() - 1)
      const month = new Date(ourDate)
      
      state.items.forEach(item => {
        const date = new Date(item.date!)
        if (date > month) res++
      })
      return res
    },

    thisYear: (state) => {
      let res = 0
      const ourDate = new Date()
      ourDate.setFullYear(ourDate.getFullYear() - 1)
      const year = new Date(ourDate)
      
      state.items.forEach(item => {
        const date = new Date(item.date!)
        if (date > year) res++
      })
      return res
    }
  }
})
